import { getClientDomain } from "@framework/config";
import { userAtom } from "@framework/config/atoms";
import { useMemo } from "react";

export function useVideoHeaders() {
  const user = userAtom.useValue();
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const accessToken = user.accessToken;

  // MUST be memoized: `ShortPlayer` lists `videoHeaders` in its hls-init
  // `useEffect` deps. Returning a fresh object every render makes that
  // effect re-fire on every render — destroying + recreating the hls.js
  // instance, cancelling in-flight segment requests, and toggling the
  // poster. The result is an infinite "first-frame ↔ cover" flicker loop
  // (segments start then cancel forever). Keying on the primitive
  // `accessToken` + `hostname` gives a stable reference across renders.
  const videoHeaders = useMemo<Record<string, string>>(() => {
    const headers: Record<string, string> = {
      Domain: getClientDomain(hostname),
    };

    // Only attach `Authorization` when there's a real token. Guests have no
    // `accessToken`; sending `Bearer undefined` makes the BE's `cachedAuth`
    // try to `jwt.verify("undefined")` and reject with 401 — which would
    // break guest streaming of free shorts (the routes now allow guests and
    // gate on resource type instead).
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }, [accessToken, hostname]);

  return {
    videoHeaders,
  };
}
